// The trade window's price chart (charts.js on the server): candles around the trade with its
// entry and exit marked, from the member's own TopstepX connection. Drawn with TradingView's
// Lightweight Charts, whose small TradingView logo on the chart is the credit it asks for.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CandlestickSeries, createChart, createSeriesMarkers, LineStyle } from 'lightweight-charts';
import { api } from './api';
import { ProLock } from './Plans';
import { useI18n } from './i18n';
import { currentLocale } from './langState';

const UNITS = [1, 5, 15];

// Whether charts are on for this member: asked once per visit.
let accessOnce = null;
const chartAccess = () => (accessOnce = accessOnce || api('/charts').catch(() => ({ available: false })));

const REASONS = {
  'not-futures': 'Charts are available for futures trades for now.',
  'time-unknown': 'This trade has no exact time, so it can’t be charted.',
  'no-data': 'TopstepX has no prices for this contract and time. Older contracts may no longer be available.',
  auth: 'TopstepX turned down your API key. Save it again in Accounts → Add account → TopstepX.',
};

// The chart's clock follows the member's computer (the library itself only knows UTC).
const local = (seconds) => seconds - new Date(seconds * 1000).getTimezoneOffset() * 60;

// Bars of the chart's candle size holding a moment.
const barTime = (date, bars) => {
  const s = Math.floor(new Date(date).getTime() / 1000);
  let hit = null;
  for (const b of bars) { if (b.t <= s) hit = b.t; else break; }
  return hit;
};

function decimals(trade) {
  const parts = [trade.entryPrice, trade.exitPrice].map((n) => (String(n).split('.')[1] || '').length);
  return Math.min(Math.max(2, ...parts), 6);
}

function Chart({ trade, bars }) {
  const { t } = useI18n();
  const box = useRef(null);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const css = getComputedStyle(document.documentElement);
    const color = (name, fallback) => css.getPropertyValue(name).trim() || fallback;
    const gain = color('--gain', '#2fe39a');
    const loss = color('--loss', '#ff5d73');
    const text = color('--text-muted', '#93a7bd');
    const grid = 'rgba(147, 167, 189, 0.08)';
    const digits = decimals(trade);
    const chart = createChart(box.current, {
      autoSize: true,
      layout: { background: { color: 'transparent' }, textColor: text, fontFamily: 'inherit', attributionLogo: true },
      grid: { vertLines: { color: grid }, horzLines: { color: grid } },
      rightPriceScale: { borderColor: grid },
      timeScale: { borderColor: grid, timeVisible: true, secondsVisible: false },
      crosshair: { vertLine: { color: text, labelBackgroundColor: '#15506a' }, horzLine: { color: text, labelBackgroundColor: '#15506a' } },
      localization: { locale: currentLocale(), priceFormatter: (p) => p.toFixed(digits) },
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: gain, downColor: loss, borderVisible: false, wickUpColor: gain, wickDownColor: loss,
      priceFormat: { type: 'price', precision: digits, minMove: 1 / 10 ** digits },
    });
    series.setData(bars.map((b) => ({ time: local(b.t), open: b.o, high: b.h, low: b.l, close: b.c })));

    // Entry and exit: arrows on their candles and a dashed line at each price.
    const long = trade.side !== 'Short';
    const entryAt = barTime(trade.date, bars);
    const exitAt = trade.exitDate ? barTime(trade.exitDate, bars) : null;
    const markers = [];
    if (entryAt !== null) markers.push({ time: local(entryAt), position: long ? 'belowBar' : 'aboveBar', shape: long ? 'arrowUp' : 'arrowDown', color: '#1fd7ef', text: t(long ? 'Buy' : 'Sell') });
    if (exitAt !== null) markers.push({ time: local(exitAt), position: long ? 'aboveBar' : 'belowBar', shape: long ? 'arrowDown' : 'arrowUp', color: '#f1f6fb', text: t('Exit') });
    createSeriesMarkers(series, markers.sort((a, b) => a.time - b.time));
    series.createPriceLine({ price: trade.entryPrice, color: '#1fd7ef', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: t('Entry') });
    if (trade.exitDate || trade.exitPrice !== trade.entryPrice) {
      series.createPriceLine({ price: trade.exitPrice, color: '#f1f6fb', lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: t('Exit') });
    }

    // Open on the trade: from a little before the entry to a little after the exit.
    const step = bars.length > 1 ? bars[1].t - bars[0].t : 60;
    const from = (entryAt ?? bars[0].t) - step * 40;
    const to = (exitAt ?? entryAt ?? bars[bars.length - 1].t) + step * 25;
    chart.timeScale().setVisibleRange({ from: local(Math.max(from, bars[0].t)), to: local(Math.min(to, bars[bars.length - 1].t)) });

    chart.subscribeCrosshairMove((param) => {
      const bar = param.time ? param.seriesData.get(series) : null;
      setHover(bar ? { o: bar.open, h: bar.high, l: bar.low, c: bar.close } : null);
    });
    return () => chart.remove();
  }, [trade, bars]); // eslint-disable-line react-hooks/exhaustive-deps

  const digits = decimals(trade);
  return (
    <div className="trade-chart-plot">
      {hover && (
        <div className="trade-chart-ohlc" aria-hidden="true">
          {['o', 'h', 'l', 'c'].map((k) => <span key={k}>{k.toUpperCase()} <b className={hover.c >= hover.o ? 'gain' : 'loss'}>{hover[k].toFixed(digits)}</b></span>)}
        </div>
      )}
      <div ref={box} className="trade-chart-canvas" role="img"
        aria-label={t('Price chart of {symbol} around this trade, with the entry at {entry} and the exit at {exit}', { symbol: trade.symbol, entry: trade.entryPrice, exit: trade.exitPrice })} />
    </div>
  );
}

export default function TradeChart({ trade }) {
  const { t } = useI18n();
  const [access, setAccess] = useState(null);
  const [unit, setUnit] = useState(1);
  const [state, setState] = useState({ loading: true });

  useEffect(() => { chartAccess().then(setAccess); }, []);
  useEffect(() => {
    if (!access || !access.available || access.locked || !trade._id) return undefined;
    let live = true;
    setState((s) => ({ ...s, loading: true, error: '' }));
    api(`/trades/${trade._id}/chart?unit=${unit}`)
      .then((r) => live && setState({ ...r, loading: false }))
      .catch((err) => live && setState({ loading: false, bars: [], error: err.message }));
    return () => { live = false; };
  }, [access, trade._id, unit]);

  if (!access || !access.available || state.reason === 'not-futures') return null;
  if (access.locked) return <div className="modal-section trade-chart"><ProLock compact text={t('Charts of your trades are part of Pro.')} /></div>;
  const bars = state.bars || [];
  return (
    <div className={`modal-section trade-chart${access.preview ? ' admin-preview-box' : ''}`}>
      <div className="modal-section-head">
        <span className="modal-section-label">
          {t('Chart')} {access.preview && <span className="admin-preview-badge">{t('Admin only')}</span>}
        </span>
        {(bars.length > 0 || state.loading) && (
          <div className="trade-chart-units" role="group" aria-label={t('Candle size')}>
            {UNITS.map((u) => (
              <button key={u} type="button" className={u === unit ? 'active' : ''} aria-pressed={u === unit} onClick={() => setUnit(u)}>{t('{n} min', { n: u })}</button>
            ))}
          </div>
        )}
      </div>
      {state.loading && !bars.length ? <div className="trade-chart-wait">{t('Loading the chart…')}</div>
        : state.error ? <p className="error">{t(state.error)}</p>
        : state.reason === 'not-connected' ? (
          <p className="field-hint">
            {t('Charts use your TopstepX connection to show the prices around your trades.')}{' '}
            <Link to="/app/accounts/new">{t('Connect TopstepX')} &rarr;</Link>
          </p>
        ) : state.reason ? <p className="field-hint">{t(REASONS[state.reason] || REASONS['no-data'])}</p>
        : bars.length ? (
          <>
            <Chart trade={trade} bars={bars} />
            <p className="field-hint trade-chart-note">
              {state.demo ? t('Sample prices in the demo. Members see the real prices from their TopstepX connection.') : t('Prices from your TopstepX connection. Times in your time zone.')}
            </p>
          </>
        ) : null}
    </div>
  );
}
